import { useFetch } from "@4uhub/lib4uhub";
import CachedRoundedIcon from "@mui/icons-material/CachedRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IDashboardGenerateExecution } from "../../../../models/dashboard-generate-execution";
import DashBoardService from "../../../../services/dashboard.service";
import {
  getDashboardGenerateExecutions,
  getDashboardPanelHasRunningExecution,
} from "../../../../services/dashboardGenerateExecution.service";
import ExecutionProgress from "../../Executions/ExecutionProgress";
import ExecutionStatusChip from "../../Executions/ExecutionStatusChip";
import { formatExecutionDateTime } from "../../Executions/utils";

const dashboardService = new DashBoardService();

const translationPath = "page.panels.executions_modal.";

// Intervalo de atualização da lista enquanto o modal está aberto
const POLLING_INTERVAL = 10000;
const PAGE_SIZE = 10;

interface IExecutionsModalProps {
  open: boolean;
  dashboardPanelId: string;
  onClose: () => void;
  onOpenDetail: (executionId: string) => void;
}

const ExecutionsModal = ({
  open,
  dashboardPanelId,
  onClose,
  onOpenDetail,
}: IExecutionsModalProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const confirm = useConfirm();

  const [executions, setExecutions] = useState<IDashboardGenerateExecution[]>(
    [],
  );
  const [hasRunning, setHasRunning] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const { sendRequest: listRequest } = useFetch(getDashboardGenerateExecutions);
  const { sendRequest: hasRunningRequest } = useFetch(
    getDashboardPanelHasRunningExecution,
  );
  const { sendRequest: syncRequest, loading: syncing } = useFetch(
    dashboardService.sync,
  );

  const fetchData = useCallback(async () => {
    const [list, running] = await Promise.all([
      listRequest({
        DashboardPanelId: dashboardPanelId,
        PageNumber: page + 1,
        PageSize: PAGE_SIZE,
      }),
      hasRunningRequest(dashboardPanelId),
    ]);

    if (list.success && list.data) {
      setExecutions(list.data.items);
      setTotalCount(list.data.totalCount);
    }
    setHasRunning(running.success ? running.data?.hasRunning === true : false);
    setLoaded(true);
  }, [dashboardPanelId, page, listRequest, hasRunningRequest]);

  useEffect(() => {
    if (!open) return;

    setLoaded(false);
    fetchData();
    const timer = setInterval(fetchData, POLLING_INTERVAL);

    return () => clearInterval(timer);
  }, [open, fetchData]);

  const syncHandler = useCallback(async () => {
    try {
      await confirm({
        confirmationButtonProps: { variant: "contained" },
        title: t("page.panels.sync_confirm.title"),
        description: t("page.panels.sync_confirm.description"),
        cancellationText: t("page.panels.sync_confirm.cancel"),
        confirmationText: t("page.panels.sync_confirm.confirm"),
      });
    } catch (error) {
      return;
    }

    const { success } = await syncRequest({ dashboardPanelId });
    if (success) setHasRunning(true);
    fetchData();
  }, [confirm, t, syncRequest, dashboardPanelId, fetchData]);

  const disableSync = !loaded || hasRunning || syncing;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t(translationPath + "title")}</DialogTitle>
      <DialogContent>
        {!loaded && <LinearProgress sx={{ mb: 2 }} />}

        {loaded && hasRunning && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(translationPath + "running_alert")}
          </Alert>
        )}

        {loaded && executions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t(translationPath + "no_data")}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("page.dashboardExecutions.code")}</TableCell>
                <TableCell>{t("page.dashboardExecutions.origin")}</TableCell>
                <TableCell>
                  {t("page.dashboardExecutions.status_label")}
                </TableCell>
                <TableCell sx={{ minWidth: 160 }}>
                  {t("page.dashboardExecutions.progress")}
                </TableCell>
                <TableCell>{t("page.dashboardExecutions.created")}</TableCell>
                <TableCell>
                  {t("page.dashboardExecutions.finished_date")}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {executions.map((execution) => (
                <TableRow key={execution.id}>
                  <TableCell>{execution.code}</TableCell>
                  <TableCell>
                    {execution.originGenerateDashboard?.name ?? "-"}
                  </TableCell>
                  <TableCell>
                    <ExecutionStatusChip status={execution.status} />
                  </TableCell>
                  <TableCell>
                    <ExecutionProgress execution={execution} />
                  </TableCell>
                  <TableCell>
                    {formatExecutionDateTime(execution.created, language)}
                  </TableCell>
                  <TableCell>
                    {formatExecutionDateTime(execution.finishedDate, language)}
                  </TableCell>
                  <TableCell padding="none">
                    <Tooltip title={t("page.dashboardExecutions.view_details")}>
                      <IconButton
                        size="small"
                        onClick={() => onOpenDetail(execution.id)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalCount > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[]}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} ${t(translationPath + "of")} ${count}`
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t(translationPath + "close")}</Button>
        <Tooltip
          title={hasRunning ? t(translationPath + "running_tooltip") : ""}
        >
          <Box component="span">
            <Button
              variant="contained"
              startIcon={<CachedRoundedIcon />}
              disabled={disableSync}
              onClick={syncHandler}
            >
              {t(translationPath + "sync")}
            </Button>
          </Box>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default memo(ExecutionsModal);
